import _ from 'underscore';
import { DateUtils } from '@bingads-webui-clientcenter/common-utils';
import type { I18nLike } from '@bingads-webui/common-types';

import {
  InsertionOrderStatus,
  IOCreationSource,
  InsertionOrdersGridFieldNames,
  UnlimitedBudget,
  EndlessDate,
  InsertionOrderLabel,
  ImmediateDate,
} from './constants/insertion-order';

const { isDateWithoutTimeEqual } = DateUtils;

interface InsertionOrderI18n extends I18nLike {
  parseDate: (value: string) => Date;
  parseDecimal: (value: string) => number;
  formatDecimalToFixed: (value: number) => string;
  formatDate: (value: Date) => string;
}

interface InsertionOrder {
  IOCreationSource?: number;
  Status?: number;
  ioValidationFields?: object;
}

interface GridInsertionOrder {
  AccountId?: number;
  AccountName?: string;
  AccountNumber?: string;
  [InsertionOrdersGridFieldNames.InsertionOrderName]?: string;
  [InsertionOrdersGridFieldNames.StartDateText]?: string;
  [InsertionOrdersGridFieldNames.EndDateText]?: string;
  [InsertionOrdersGridFieldNames.StatusValue]?: number;
  [InsertionOrdersGridFieldNames.IsInsertionOrderNameEditable]?: boolean;
  [InsertionOrdersGridFieldNames.IsStartDateEditable]?: boolean;
  [InsertionOrdersGridFieldNames.IsEndDateEditable]?: boolean;
  [InsertionOrdersGridFieldNames.IsSpendCapAmountEditable]?: boolean;
  [InsertionOrdersGridFieldNames.IsAgencyIdOrPoEditable]?: boolean;
  [InsertionOrdersGridFieldNames.IsDealPointIdEditable]?: boolean;
  [InsertionOrdersGridFieldNames.IsRecordCenterIdEditable]?: boolean;
  [InsertionOrdersGridFieldNames.IsCommentEditable]?: boolean;
}

/**
 * Get whether this insertion order is pending under takeover billing linked accounts
 * @param {object} insertionOrder Value object of insertion order
 * @returns {bool} Returns true if this insertion order is pending under takeover billing linked accounts.
 */
export const isPendingTakeoverBillingIO = (insertionOrder?: InsertionOrder) => _.isObject(insertionOrder) &&
  insertionOrder.IOCreationSource === IOCreationSource.BillingTakeOver &&
  (insertionOrder.Status === InsertionOrderStatus.PendingBillToPartyChange ||
  insertionOrder.Status === InsertionOrderStatus.PendingBillToPartyChangeAndUserReview);

/**
 * Returns a copy of the given in object with additional utility methods.
 * @param {object} insertionOrder The insertion order object to be extended with additional utility methods.
 * @returns {object} A copy of the extended insertion order object.
 */
export const extendInsertionOrder = (insertionOrder: InsertionOrder = {}) => _.defaults({}, insertionOrder, {
  isPendingTakeoverBillingIO: () => isPendingTakeoverBillingIO(insertionOrder),
});

/**
 * Returns an insertion order which matches singleIOForm' format.
 * @param {object} selectedIO The insertion order from IO grid.
 * @param {object} i18n The i18n object.
 * @returns {object} An insertion order which matches singleIOForm' format.
 */
export const convertGridIOToSingleIOFormIO = (selectedIO?: GridInsertionOrder, i18n?: Pick<InsertionOrderI18n, 'parseDate'>) => {
  if (!_.isObject(selectedIO) || !_.isObject(i18n) || !_.isFunction(i18n.parseDate)) {
    return {};
  }

  return ({
    account: { AccountId: selectedIO.AccountId, DisplayAccountName: `${selectedIO.AccountName}(${selectedIO.AccountNumber})` },
    defaultIO: {
      ...selectedIO,
      FriendlyName: _.isString(selectedIO[InsertionOrdersGridFieldNames.InsertionOrderName]) ? selectedIO[InsertionOrdersGridFieldNames.InsertionOrderName].trim() : null,
      FromDate: selectedIO[InsertionOrdersGridFieldNames.StartDateText] && i18n.parseDate(selectedIO[InsertionOrdersGridFieldNames.StartDateText]),
      ThruDate: selectedIO[InsertionOrdersGridFieldNames.EndDateText] && i18n.parseDate(selectedIO[InsertionOrdersGridFieldNames.EndDateText]),
      Status: selectedIO[InsertionOrdersGridFieldNames.StatusValue],
    },
    mtIOEditableMap: {
      FriendlyName: selectedIO[InsertionOrdersGridFieldNames.IsInsertionOrderNameEditable],
      FromDate: selectedIO[InsertionOrdersGridFieldNames.IsStartDateEditable],
      ThruDate: selectedIO[InsertionOrdersGridFieldNames.IsEndDateEditable],
      SpendCapAmount: selectedIO[InsertionOrdersGridFieldNames.IsSpendCapAmountEditable],
      AgencyIdOrPo: selectedIO[InsertionOrdersGridFieldNames.IsAgencyIdOrPoEditable],
      DealPointId: selectedIO[InsertionOrdersGridFieldNames.IsDealPointIdEditable],
      RecordCenterId: selectedIO[InsertionOrdersGridFieldNames.IsRecordCenterIdEditable],
      Comment: selectedIO[InsertionOrdersGridFieldNames.IsCommentEditable],
    },
  });
};

/**
 * Returns an insertion order whether is valid.
 * @param {object} ioValidationFields The insertion order validation result object from IO grid.
 * @param {object} isIOItemNeedValidation The object of which IO items should be verified.
 * @returns {bool} Whether is a valid IO.
 */
export const isIOValid = <Field extends string>({
  ioValidationFields,
  isIOItemNeedValidation,
}: {
  ioValidationFields?: Partial<Record<Field, boolean>>;
  isIOItemNeedValidation: Partial<Record<Field, boolean>>;
}) =>
  // when isIOItemNeedValidation[key] === false, it means we don't need this io item
  // and it does not need validation
    (_.every as typeof _.every & ((
    collection: Partial<Record<Field, boolean>>,
    predicate: (value: boolean, key: Field) => boolean
  ) => boolean))(isIOItemNeedValidation, (value: boolean, key: Field) =>
      !value || (ioValidationFields && ioValidationFields[key]) as boolean);


/**
 * Returns insertion orders whether are all valid.
 * @param {object} insertionOrders The insertion orders.
 * @param {object} isIOItemNeedValidation The object of which IO items should be verified.
 * @returns {bool} Whether are valid IOs.
 */
export const isAllIOsValid = <Field extends string>({
  insertionOrders,
  isIOItemNeedValidation,
}: {
  insertionOrders?: Array<{ ioValidationFields?: Partial<Record<Field, boolean>> }>;
  isIOItemNeedValidation: Partial<Record<Field, boolean>>;
}) =>
    _.every(insertionOrders, (insertionOrder: { ioValidationFields?: Partial<Record<Field, boolean>> }) => isIOValid({ ioValidationFields: insertionOrder.ioValidationFields, isIOItemNeedValidation }));

/**
 * Returns insertion orders budget whether is unlimited.
 * @param {number} spendCapAmountValue The insertion orders budget.
 * @returns {bool} Insertion orders budget whether is unlimited.
 */
export const isUnlimitedIOBudget = (spendCapAmountValue?: number) => spendCapAmountValue === UnlimitedBudget;

/**
 * Returns insertion orders endDate whether is endless.
 * @param {object} endDateValue The insertion orders endDate.
 * @returns {bool} Whether insertion orders end date  is endless.
 */
export const isEndlessIOEndDate = (endDateValue?: Date) => isDateWithoutTimeEqual(endDateValue, EndlessDate);

/**
 * Returns whether insertion orders startDate is immediate.
 * @param {object} startDateValue The insertion orders startDate.
 * @returns {bool} Whether insertion orders start date is immediate.
 */
export const isImmediateStartDate = (startDateValue?: Date) => isDateWithoutTimeEqual(startDateValue, ImmediateDate);

/**
 * Returns insertion orders budget number.
 * @param {string} spendCapAmountValue The insertion orders budget string or budget number.
 * @param {object} i18n The i18n object.
 * @returns {number} Insertion orders budget number.
 */
export const getSpendCapAmountNumber = ({
  spendCapAmountValue,
  i18n,
}: { spendCapAmountValue?: string | number; i18n: InsertionOrderI18n }) => {
  if (_.isString(spendCapAmountValue)) {
    return i18n.parseDecimal(spendCapAmountValue);
  } else if (_.isNumber(spendCapAmountValue)) {
    return spendCapAmountValue;
  }
  return null;
};

/**
 * Returns insertion orders budget string in grid.
 * @param {number} spendCapAmountValue The insertion orders budget.
 * @param {object} i18n The i18n object.
 * @returns {bool} Insertion orders budget string.
 */
export const formatUnlimitedIOBudgetInGrid = ({
  spendCapAmountValue,
  i18n,
  unlimitedStringKey = _TL_('Unlimited'),
}: { spendCapAmountValue?: string | number; i18n: InsertionOrderI18n; unlimitedStringKey?: string }) => {
  const spendCapAmountNumber = getSpendCapAmountNumber({ spendCapAmountValue, i18n });

  if (isUnlimitedIOBudget(spendCapAmountNumber)) {
    return i18n.getString(unlimitedStringKey);
  }
  return _.isNull(spendCapAmountNumber) ? null : i18n.formatDecimalToFixed(spendCapAmountNumber);
};

/**
 * Returns insertion orders endDate string in grid.
 * @param {object} endDateValue The insertion orders endDate.
 * @param {object} i18n The i18n object.
 * @returns {bool} Insertion orders endDate string.
 */
export const formatEndlessIOEndDateInGrid = ({
  endDateValue,
  i18n,
  noEndDateStringKey = _TL_('No End Date'),
}: { endDateValue?: Date; i18n: InsertionOrderI18n; noEndDateStringKey?: string }) => {
  if (isEndlessIOEndDate(endDateValue)) {
    return i18n.getString(noEndDateStringKey);
  }
  return _.isDate(endDateValue) ? i18n.formatDate(endDateValue) : null;
};

/**
 * Returns insertion orders endDate string in grid.
 * @param {object} i18n The i18n  object.
 * @param {object} iolabel The insertion orders alert label.
 * @param {object} column The insertion orders column name.
 * @returns {object} Insertion orders alert status.
 */
export const getIOAlertInGrid = ({
  i18n,
  column,
  iolabel,
}: {
  i18n: InsertionOrderI18n;
  column?: typeof InsertionOrdersGridFieldNames.EndDate | typeof InsertionOrdersGridFieldNames.BalanceAmount;
  iolabel?: number;
}) => {
  const alert = {
    EndDate: {
      [InsertionOrderLabel.Expiring]: {
        alertStatus: i18n.getString(_TL_('Expiring')),
        alertColor: 'status-warning',
        alertIcon: 'Warning',
      },
      [InsertionOrderLabel.Expired]: {
        alertStatus: i18n.getString(_TL_('Expired')),
        alertColor: 'status-error',
        alertIcon: 'Blocked2',
      },
    },
    BalanceAmount: {
      [InsertionOrderLabel.Exhausting]: {
        alertStatus: i18n.getString(_TL_('Exhausting')),
        alertColor: 'status-warning',
        alertIcon: 'Warning',
      },
      [InsertionOrderLabel.NoBudget]: {
        alertStatus: i18n.getString(_TL_('No budget')),
        alertColor: 'status-error',
        alertIcon: 'Blocked2',
      },
    },
  };
  return ((column === InsertionOrdersGridFieldNames.EndDate || column === InsertionOrdersGridFieldNames.BalanceAmount) && iolabel) ? (alert as Partial<Record<
    typeof InsertionOrdersGridFieldNames.EndDate | typeof InsertionOrdersGridFieldNames.BalanceAmount,
    Partial<Record<number, { alertStatus: string; alertColor: string; alertIcon: string }>>
  >>)[column][iolabel] : undefined;
};

/**
 * parse decimal from number or string or others.
 * @param {object} i18n The i18n  object.
 * @param {object} value The value to parse.
 * @returns {number} a number parsed from value.
 */
export function getDecimal({
  i18n,
  value,
}: { i18n: InsertionOrderI18n; value?: string | number }) {
  if (_.isNumber(value)) {
    return value;
  }
  if (_.isString(value)) {
    return i18n.parseDecimal(value);
  }
  return NaN;
}
